/**
 * "When will products I just added to the selection push?" — read from the
 * `syncSchedule` the integrations list serves (`resolveNextScheduledSync` on
 * the API, the same rule the scheduler uses), and turned into copy here.
 */

export type NextScheduledSyncReason = 'scheduled' | 'due' | 'never_synced' | 'manual' | 'auto_sync_off' | 'blocked' | 'inactive';

export interface SyncScheduleDto {
  interval: 'hourly' | 'daily' | 'weekly' | 'manual';
  autoSync: boolean;
  nextScheduledSyncAt: string | null;
  dueNow: boolean;
  reason: NextScheduledSyncReason;
  schedulerTickMinutes: number;
  /** Flag `scheduled_sync_time`: the last slot the scheduler claimed; present only with the flag on. */
  lastSyncSlotAt?: string | null;
  /** Flag `scheduled_sync_time`: present when the integration runs at a chosen local time. */
  syncTime?: { time: string; weekday: number | null; timeZone: string; slotAt: string | null; missedSlotAt: string | null };
}

export interface ScheduleIntegrationLike {
  id: string;
  platform?: string;
  status?: string;
  isActive?: boolean;
  syncSchedule?: SyncScheduleDto | null;
}

export interface NextSyncCopy {
  /** Fits a chip or a timestamp line: "next sync in ~3h". */
  short: string;
  /** A full sentence for a note or toast. */
  detail: string;
  /** True when the schedule will run it without the store doing anything. */
  automatic: boolean;
}

const PLATFORM_NAMES: Record<string, string> = {
  shopify: 'Shopify',
  lightspeed: 'Lightspeed',
  lightspeed_ecom: 'Lightspeed eCom',
  bigcommerce: 'BigCommerce',
  woocommerce: 'WooCommerce',
};

export const platformDisplayName = (platform: string | undefined): string =>
  (platform && PLATFORM_NAMES[platform.toLowerCase()]) || 'store';

/**
 * The integration whose schedule the store page should quote: the one named
 * (an integration's own selection), else the active Shopify connector the
 * dashboard also reads, else any active integration, else the first.
 */
export function pickScheduleIntegration<T extends ScheduleIntegrationLike>(integrations: T[], preferredId?: string | null): T | null {
  if (!integrations.length) return null;
  if (preferredId) {
    const named = integrations.find((i) => i.id === preferredId);
    if (named) return named;
  }
  const isActive = (i: T) => i.status === 'active' || i.isActive === true;
  return (
    integrations.find((i) => i.platform === 'shopify' && isActive(i)) ||
    integrations.find((i) => i.platform === 'shopify') ||
    integrations.find(isActive) ||
    integrations[0]
  );
}

/** "~3h" / "~40m" / "~2d" — coarse on purpose; the scheduler only ticks every 15 minutes. */
export function formatApproxWait(ms: number, tickMinutes = 15): { short: string; long: string } {
  const minutes = Math.max(0, Math.round(ms / 60000));
  if (minutes < tickMinutes) return { short: 'soon', long: `within ${tickMinutes} minutes` };
  if (minutes < 60) return { short: `~${minutes}m`, long: `in about ${minutes} minutes` };
  const hours = Math.round(minutes / 60);
  if (hours < 36) return { short: `~${hours}h`, long: `in about ${hours} hour${hours === 1 ? '' : 's'}` };
  const days = Math.round(hours / 24);
  return { short: `~${days}d`, long: `in about ${days} day${days === 1 ? '' : 's'}` };
}

const INTERVAL_WORD: Record<SyncScheduleDto['interval'], string> = {
  hourly: 'hourly',
  daily: 'daily',
  weekly: 'weekly',
  manual: 'manual',
};

/**
 * Copy for a schedule, or null when there is nothing to say (no schedule
 * served, or the integration is inactive).
 */
export function describeNextScheduledSync(
  schedule: SyncScheduleDto | null | undefined,
  platform?: string,
  now: number = Date.now(),
): NextSyncCopy | null {
  if (!schedule) return null;
  const name = platformDisplayName(platform);
  const tick = schedule.schedulerTickMinutes || 15;
  switch (schedule.reason) {
    case 'inactive':
      return null;
    case 'manual':
    case 'auto_sync_off':
      return {
        short: 'automatic sync off',
        detail: `Automatic ${name} sync is off — products in the selection push when you run Sync now from Integrations.`,
        automatic: false,
      };
    case 'blocked':
      return {
        short: 'scheduled sync paused',
        detail: `Scheduled ${name} sync is paused because product and pricing sync are both off for this integration.`,
        automatic: false,
      };
    case 'due':
    case 'never_synced':
      return {
        short: 'next sync starting soon',
        detail: `A scheduled ${name} sync is due and starts within ${tick} minutes.`,
        automatic: true,
      };
    case 'scheduled':
    default: {
      const at = schedule.nextScheduledSyncAt ? new Date(schedule.nextScheduledSyncAt).getTime() : NaN;
      if (Number.isNaN(at)) return null;
      const wait = formatApproxWait(at - now, tick);
      const cadence = INTERVAL_WORD[schedule.interval] || schedule.interval;
      return {
        short: `next sync ${wait.short === 'soon' ? 'starting soon' : `in ${wait.short}`}`,
        detail: `Next scheduled ${name} sync ${wait.long} (${cadence}) — products added to the selection push then, or sooner with Sync now from Integrations.`,
        automatic: true,
      };
    }
  }
}
